import { useState, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Copy, RefreshCw, Trash2, Users, User as UserIcon } from 'lucide-react'
import { api, PublicFileAccessLog, PublicFileLink, ShareWithDetails, SharePermission, User, Team, OrgMember } from '@/services/api'

interface ShareModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  resourceType: 'file' | 'folder'
  resourceId: string
  resourceName: string
  orgId: string
}

export function ShareModal({ open, onOpenChange, resourceType, resourceId, resourceName, orgId }: ShareModalProps) {

  const [shares, setShares] = useState<ShareWithDetails[]>([])
  const [loading, setLoading] = useState(false)
  const [orgMembers, setOrgMembers] = useState<User[]>([])
  const [teams, setTeams] = useState<Team[]>([])

  const [targetType, setTargetType] = useState<'user' | 'team'>('user')
  const [targetId, setTargetId] = useState<string>('')
  const [permission, setPermission] = useState<SharePermission>('view')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [publicLinks, setPublicLinks] = useState<PublicFileLink[]>([])
  const [publicLabel, setPublicLabel] = useState('')
  const [creatingPublic, setCreatingPublic] = useState(false)
  const [publicLogs, setPublicLogs] = useState<PublicFileAccessLog[]>([])
  const [loadingPublicLogs, setLoadingPublicLogs] = useState(false)

  const loadShares = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const result = resourceType === 'file'
        ? await api.shares.listForFile(orgId, resourceId)
        : await api.shares.listForFolder(orgId, resourceId)
      setShares(result.shares || [])
    } catch (err) {
      console.error('Failed to load shares:', err)
    } finally {
      setLoading(false)
    }
  }, [orgId, resourceType, resourceId])

  const loadOrgMembers = useCallback(async () => {
    if (!orgId) return
    try {
      const result = await api.orgs.members.list(orgId)
      const users: User[] = (result.members || []).map((m: OrgMember) => ({
        id: m.user_id,
        email: m.email || '',
        display_name: m.display_name || m.email || 'Unknown',
        created_at: m.created_at,
      }))
      setOrgMembers(users)
    } catch (err) {
      console.error('Failed to load org members:', err)
    }
  }, [orgId])

  const loadPublicLinks = useCallback(async () => {
    if (!orgId || resourceType !== 'file') return
    try {
      const res = await api.shares.listPublicLinksForFile(orgId, resourceId)
      setPublicLinks(res.links || [])
    } catch (err) {
      console.error('Failed to load public links:', err)
      setPublicLinks([])
    }
  }, [orgId, resourceId, resourceType])

  const loadTeams = useCallback(async () => {
    if (!orgId) return
    try {
      const result = await api.teams.list(orgId)
      setTeams(result.teams || [])
    } catch (err) {
      console.error('Failed to load teams:', err)
    }
  }, [orgId])

  useEffect(() => {
    if (open && orgId) {
      loadShares()
      loadOrgMembers()
      loadTeams()
      if (resourceType === 'file') {
        loadPublicLinks()
      }
    }
  }, [open, orgId, resourceId, resourceType, loadShares, loadOrgMembers, loadTeams, loadPublicLinks])

  const handleCreate = async () => {
    if (!orgId || !targetId) return
    setCreating(true)
    setError(null)
    try {
      const data: Parameters<typeof api.shares.create>[1] = {
        permission,
      }
      if (resourceType === 'file') {
        data.file_id = resourceId
      } else {
        data.folder_id = resourceId
      }
      if (targetType === 'user') {
        data.target_user_id = targetId
      } else {
        data.target_team_id = targetId
      }

      await api.shares.create(orgId, data)
      setTargetId('')
      loadShares()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create share'
      setError(message)
    } finally {
      setCreating(false)
    }
  }

  const handleUpdatePermission = async (shareId: string, newPermission: SharePermission) => {
    if (!orgId) return
    try {
      await api.shares.update(orgId, shareId, { permission: newPermission })
      loadShares()
    } catch (err) {
      console.error('Failed to update share:', err)
    }
  }

  const handleDelete = async (shareId: string) => {
    if (!orgId) return
    try {
      await api.shares.delete(orgId, shareId)
      loadShares()
    } catch (err) {
      console.error('Failed to delete share:', err)
    }
  }

  const availableTargets = targetType === 'user' ? orgMembers : teams
  const hasEnabledPublicLinks = publicLinks.some((link) => link.enabled)

  const handleCreatePublicLink = async () => {
    if (!orgId || resourceType !== 'file') return
    try {
      setCreatingPublic(true)
      await api.shares.createPublicLinkForFile(orgId, resourceId, { label: publicLabel.trim() || undefined })
      setPublicLabel('')
      await loadPublicLinks()
    } catch (err) {
      console.error('Failed to create public link:', err)
    } finally {
      setCreatingPublic(false)
    }
  }

  const handleTogglePublicLink = async (link: PublicFileLink) => {
    if (!orgId || resourceType !== 'file') return
    try {
      await api.shares.updatePublicLinkForFile(orgId, resourceId, link.id, { enabled: !link.enabled })
      await loadPublicLinks()
    } catch (err) {
      console.error('Failed to update public link:', err)
    }
  }

  const handleRegeneratePublicLink = async (link: PublicFileLink) => {
    if (!orgId || resourceType !== 'file') return
    try {
      await api.shares.regeneratePublicLinkForFile(orgId, resourceId, link.id)
      await loadPublicLinks()
    } catch (err) {
      console.error('Failed to regenerate public link:', err)
    }
  }

  const handleDisableAllPublicLinks = async () => {
    if (!orgId || resourceType !== 'file') return
    try {
      await api.shares.disableAllPublicLinksForFile(orgId, resourceId)
      await loadPublicLinks()
    } catch (err) {
      console.error('Failed to disable public links:', err)
    }
  }

  const handleCopyPublicLink = async (token: string) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/share/${token}`)
    } catch (err) {
      console.error('Failed to copy public link:', err)
    }
  }

  const handleLoadPublicLogs = async () => {
    if (!orgId || resourceType !== 'file') return
    try {
      setLoadingPublicLogs(true)
      const res = await api.shares.listPublicAccessLogsForFile(orgId, resourceId)
      setPublicLogs(res.logs || [])
    } catch (err) {
      console.error('Failed to load public access logs:', err)
      setPublicLogs([])
    } finally {
      setLoadingPublicLogs(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share "{resourceName}"</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add new share */}
          <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Share with</Label>
                <Select value={targetType} onValueChange={(v) => { setTargetType(v as 'user' | 'team'); setTargetId('') }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">
                      <div className="flex items-center gap-2 shrink-0">
                        <UserIcon className="h-4 w-4" />
                        Person
                      </div>
                    </SelectItem>
                    <SelectItem value="team">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Team
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Permission</Label>
                <Select value={permission} onValueChange={(v) => setPermission(v as SharePermission)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">View only</SelectItem>
                    <SelectItem value="edit">Can edit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{targetType === 'user' ? 'Select person' : 'Select team'}</Label>
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger>
                  <SelectValue placeholder={`Choose a ${targetType}...`} />
                </SelectTrigger>
                <SelectContent>
                  {targetType === 'user' ? (
                    orgMembers.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.display_name} ({user.email})
                      </SelectItem>
                    ))
                  ) : (
                    teams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name}
                      </SelectItem>
                    ))
                  )}
                  {availableTargets.length === 0 && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No {targetType === 'user' ? 'members' : 'teams'} available
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <Button onClick={handleCreate} disabled={!targetId || creating} className="w-full">
              {creating ? 'Sharing...' : 'Share'}
            </Button>
          </div>

          {/* Existing shares */}
          <div className="space-y-2">
            <Label>Shared with</Label>
            {loading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : shares.length === 0 ? (
              <p className="text-sm text-muted-foreground">Not shared with anyone yet</p>
            ) : (
              <div className="space-y-2">
                {shares.map((share) => (
                  <div key={share.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border rounded-lg">
                    <div className="flex items-center gap-3 min-w-0">
                      {share.target_user ? (
                        <>
                          <UserIcon className="h-5 w-5 text-muted-foreground" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{share.target_user.display_name}</p>
                            <p className="text-sm text-muted-foreground truncate">{share.target_user.email}</p>
                          </div>
                        </>
                      ) : share.target_team ? (
                        <>
                          <Users className="h-5 w-5 text-muted-foreground" />
                          <div className="min-w-0">
                            <p className="font-medium truncate">{share.target_team.name}</p>
                            <p className="text-sm text-muted-foreground">Team</p>
                          </div>
                        </>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={share.permission}
                        onValueChange={(v) => handleUpdatePermission(share.id, v as SharePermission)}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">View only</SelectItem>
                          <SelectItem value="edit">Can edit</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(share.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {resourceType === 'file' && (
            <div className="space-y-3 pt-2 border-t">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <Label>Public Links</Label>
                {hasEnabledPublicLinks && (
                  <Button size="sm" variant="outline" onClick={handleDisableAllPublicLinks}>
                    Stop Sharing Publicly
                  </Button>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={publicLabel}
                  onChange={(e) => setPublicLabel(e.target.value)}
                  placeholder="Optional label (e.g. Vendor A)"
                />
                <Button size="sm" className="sm:w-auto" onClick={handleCreatePublicLink} disabled={creatingPublic}>
                  {creatingPublic ? 'Creating...' : 'Add Link'}
                </Button>
              </div>

              <div className="space-y-2">
                {publicLinks.map((link) => (
                  <div key={link.id} className="border rounded-md p-2.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{link.label || 'Unnamed Link'}</p>
                      <p className="text-xs text-muted-foreground truncate">{window.location.origin}/share/{link.token}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 self-start sm:self-center">
                      <Button variant="ghost" size="icon" onClick={() => handleCopyPublicLink(link.token)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleRegeneratePublicLink(link)}>
                        <RefreshCw className="h-4 w-4" />
                      </Button>
                      <Button variant={link.enabled ? 'default' : 'secondary'} size="sm" onClick={() => handleTogglePublicLink(link)}>
                        {link.enabled ? 'Enabled' : 'Disabled'}
                      </Button>
                    </div>
                  </div>
                ))}
                {publicLinks.length === 0 && <p className="text-sm text-muted-foreground">No public links yet.</p>}
              </div>

              <div className="space-y-2">
                <Button variant="outline" size="sm" onClick={handleLoadPublicLogs}>
                  View Access Logs
                </Button>
                {loadingPublicLogs ? (
                  <p className="text-xs text-muted-foreground">Loading logs...</p>
                ) : publicLogs.length > 0 ? (
                  <div className="max-h-36 overflow-auto space-y-1 text-xs border rounded-md p-2">
                    {publicLogs.map((log) => (
                      <div key={log.id} className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground truncate">{log.ip_address || 'Unknown IP'}</span>
                        <span>{new Date(log.created_at).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
